import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ProfileTestID } from "@/constants/TestID";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetContactPreferencesQuery,
  useGetUsStatesQuery,
} from "@/store/api/userEnums";
import {
  AddressLine1Field,
  AddressLine2Field,
  CellPhoneField,
  CityField,
  ContactPreferencesField,
  EmailField,
  StateField,
  ZipField,
} from "@/ui/components/profile/edit/ContactInfoFields";
import { Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import {
  enumsToMultiSelectOptions,
  usStatesEnumToMultiSelectOptions,
} from "@/utils/api/enums.util";
import type { PermissionChecker } from "@/utils/profile/permissions.util";

const baseTestID = ProfileTestID.ContactInfo;

interface ContactInfoSectionProps extends UserContactInfo {
  onChange: (contactInfo: UserContactInfo) => void;
  userPermissions: PermissionChecker;
}

/* eslint max-lines-per-function: ["error", {max: 150}] -- 8 distinct sub-components for this section grew this too large */
const ContactInfoSection = ({
  addressLine1: initialAddressLine1,
  addressLine2: initialAddressLine2,
  cellPhone: initialCellPhone,
  city: initialCity,
  contactPreferences: initialContactPreferences,
  email: initialEmail,
  onChange,
  state: initialState,
  userPermissions,
  zip: initialZip,
}: ContactInfoSectionProps) => {
  const { track } = useAnalytics();
  const trackFieldPressIn = useCallback(
    (field: string) => {
      const fieldName = field.replace("Field", "");
      track(ProfileAnalyticsEvent.EditProfileFieldPressed(fieldName), {
        [AnalyticsFieldName.Component]: ContactInfoSection.name,
        [AnalyticsFieldName.Field]: fieldName,
        [AnalyticsFieldName.Source]: ModalPath.EditProfile,
      });
    },
    [track],
  );

  const [addressLine1, setAddressLine1] = useState<null | string | undefined>(
    initialAddressLine1,
  );
  const [addressLine2, setAddressLine2] = useState<null | string | undefined>(
    initialAddressLine2,
  );
  const [cellPhone, setCellPhone] = useState<string>(initialCellPhone);
  const [contactPreferences, setContactPreferences] = useState<
    ContactPreference[] | undefined
  >(initialContactPreferences);
  const [city, setCity] = useState<null | string | undefined>(initialCity);
  const [email, setEmail] = useState<string>(initialEmail);
  const [state, setState] = useState<null | string | undefined>(initialState);
  const [zip, setZip] = useState<null | string | undefined>(initialZip);
  useEffect(() => {
    const contactInfo = {
      addressLine1,
      addressLine2,
      cellPhone,
      city,
      contactPreferences,
      email,
      state,
      zip,
    };
    onChange(contactInfo);
  }, [
    addressLine1,
    addressLine2,
    contactPreferences,
    cellPhone,
    city,
    email,
    state,
    zip,
    onChange,
  ]);

  const { data: usStatesData } = useGetUsStatesQuery();
  const stateOptions = useMemo(
    () => usStatesEnumToMultiSelectOptions(usStatesData),
    [usStatesData],
  );

  const { data: contactPreferencesData } = useGetContactPreferencesQuery();
  const contactPreferenceOptions = useMemo(
    () =>
      enumsToMultiSelectOptions({
        enumOptions: contactPreferencesData,
        valueField: "label",
      }),
    [contactPreferencesData],
  );

  return (
    <VStack gap={12}>
      <Text testID={baseTestID} variant="h5">
        Contact info
      </Text>
      <EmailField
        email={email}
        setEmail={setEmail}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <CellPhoneField
        cellPhone={cellPhone}
        setCellPhone={setCellPhone}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <AddressLine1Field
        addressLine1={addressLine1}
        setAddressLine1={setAddressLine1}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <AddressLine2Field
        addressLine2={addressLine2}
        setAddressLine2={setAddressLine2}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <CityField
        city={city}
        setCity={setCity}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
      <HStack gap={24}>
        <Box flex={1}>
          <StateField
            options={stateOptions}
            setState={setState}
            state={state}
            trackFieldPressIn={trackFieldPressIn}
            userPermissions={userPermissions}
          />
        </Box>
        <Box flex={1}>
          <ZipField
            setZip={setZip}
            trackFieldPressIn={trackFieldPressIn}
            userPermissions={userPermissions}
            zip={zip}
          />
        </Box>
      </HStack>
      <ContactPreferencesField
        contactPreferences={contactPreferences}
        options={contactPreferenceOptions}
        setContactPreferences={setContactPreferences}
        trackFieldPressIn={trackFieldPressIn}
        userPermissions={userPermissions}
      />
    </VStack>
  );
};

export type { ContactInfoSectionProps };
export default ContactInfoSection;
